/**
 * O vídeo 360 que o cliente exportou vai TOCAR aqui?
 *
 * Descobrir isso depois do upload é o pior momento possível: um 360 de
 * 8K tem gigabytes, a pessoa espera a barra encher e só então recebe um
 * erro. Pior ainda quando a mensagem culpa o container (.mkv) enquanto o
 * problema real é o codec ou a resolução.
 *
 * Duas barreiras derrubam vídeo de câmera 360 no navegador, e a UI antiga
 * não checava nenhuma:
 *
 *  1. CODEC — H.265/HEVC é o padrão de export da Insta360 e do GoPro Max.
 *     Safari decodifica; Chrome só em condições específicas de hardware;
 *     Firefox nunca. Não existe teste de tabela confiável, então aqui o
 *     teste é EMPÍRICO: pede ao navegador para decodificar o arquivo de
 *     verdade e observa se saiu um quadro.
 *
 *  2. RESOLUÇÃO — o vídeo vira TEXTURA WebGL na esfera. Acima de
 *     gl.MAX_TEXTURE_SIZE o quadro não sobe para a GPU e a cena fica
 *     preta mesmo com o vídeo "tocando". Desktop costuma dar 16384, mas
 *     celular ainda entrega 4096 — por isso 8K some no telefone do
 *     cliente mesmo quando abre no computador de quem publicou.
 */

export type VideoIssue = 'codec' | 'resolution' | 'both' | null;

export interface VideoProbe {
  ok: boolean;
  issue: VideoIssue;
  width: number;
  height: number;
  /** Maior textura que a GPU deste aparelho aceita (px). */
  maxTexture: number;
}

/** Limite de textura da GPU — o teto real do que a esfera consegue exibir. */
export function maxTextureSize(): number {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl2') ?? canvas.getContext('webgl')) as WebGLRenderingContext | null;
    if (!gl) return 4096;
    const size = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    return typeof size === 'number' && size > 0 ? size : 4096;
  } catch {
    return 4096;
  }
}

/**
 * Publicar é diferente de assistir: o tour vai rodar em celular alheio,
 * não só na máquina que subiu. 4096 é o teto que atravessa a base
 * instalada inteira — acima disso o vídeo funciona para quem publicou e
 * some para parte de quem visita.
 */
export const SAFE_VIDEO_WIDTH = 4096;

/**
 * Decodifica o arquivo LOCALMENTE e responde se ele serve.
 *
 * Não confia em canPlayType (que responde "maybe" com frequência e mente
 * sobre HEVC): monta um <video>, deixa o navegador decodificar e só
 * aceita se um quadro real existir — videoWidth só é preenchido quando a
 * decodificação de fato aconteceu.
 */
export function probeVideo(file: File, timeoutMs = 15000): Promise<VideoProbe> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;

    const finish = (ok: boolean, width: number, height: number) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);

      const maxTexture = maxTextureSize();
      const limit = Math.min(maxTexture, SAFE_VIDEO_WIDTH);
      const tooBig = width > limit;
      const issue: VideoIssue = !ok && tooBig ? 'both' : !ok ? 'codec' : tooBig ? 'resolution' : null;
      resolve({ ok: ok && !tooBig, issue, width, height, maxTexture });
    };

    // Arquivo enorme em disco lento pode demorar; o timeout responde como
    // "não deu" em vez de deixar a pessoa esperando sem retorno.
    const timer = window.setTimeout(() => finish(false, video.videoWidth || 0, video.videoHeight || 0), timeoutMs);

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    // loadeddata (e não loadedmetadata): metadata sai do container, que o
    // navegador lê mesmo sem saber decodificar o codec lá dentro.
    video.onloadeddata = () => finish(video.videoWidth > 0, video.videoWidth, video.videoHeight);
    video.onerror = () => finish(false, 0, 0);
    video.src = url;
    video.load();
  });
}
